import { useState, useEffect, useCallback } from "react";
import { loadFromStorage, saveToStorage } from "@/lib/storage";
import type { GeoCoordinates } from "@/types/location";

type GeoStatus = "idle" | "requesting" | "granted" | "denied" | "unavailable";

interface UseGeolocationReturn {
  coords: GeoCoordinates | null;
  status: GeoStatus;
  error: string | null;
  refresh: () => void;
  updateCoords: (coords: GeoCoordinates) => void;
}

const STORAGE_KEY = "miqat:coords";

export function useGeolocation(): UseGeolocationReturn {
  const [coords, setCoords] = useState<GeoCoordinates | null>(() =>
    loadFromStorage<GeoCoordinates>(STORAGE_KEY)
  );
  const [status, setStatus] = useState<GeoStatus>(
    coords ? "granted" : "idle"
  );
  const [error, setError] = useState<string | null>(null);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus("unavailable");
      setError("Geolocation is not supported by this browser");
      return;
    }

    setStatus("requesting");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newCoords: GeoCoordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setCoords(newCoords);
        saveToStorage(STORAGE_KEY, newCoords);
        setStatus("granted");
        setError(null);
        // Resolve nearest city name
        import("@/lib/cities").then(({ findNearestCity }) => {
          const city = findNearestCity(newCoords.latitude, newCoords.longitude);
          const withName = { ...newCoords, name: `${city.name}, ${city.country}` };
          setCoords(withName);
          saveToStorage(STORAGE_KEY, withName);
        });
      },
      (err) => {
        setStatus("denied");
        setError(err.message);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  useEffect(() => {
    if (!coords) {
      requestLocation();
    } else if (!coords.name) {
      // Resolve name for saved coords missing it (migration)
      import("@/lib/cities").then(({ findNearestCity }) => {
        const city = findNearestCity(coords.latitude, coords.longitude);
        const withName = { ...coords, name: `${city.name}, ${city.country}` };
        setCoords(withName);
        saveToStorage(STORAGE_KEY, withName);
      });
    }
  }, [coords, requestLocation]);

  const updateCoords = useCallback((newCoords: GeoCoordinates) => {
    setCoords(newCoords);
    saveToStorage(STORAGE_KEY, newCoords);
    setStatus("granted");
    setError(null);
  }, []);

  return { coords, status, error, refresh: requestLocation, updateCoords };
}
